import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { ReactElement, ReactNode, cloneElement, useId } from 'react'

import { breakpoints } from 'src/theme/tokens.generated'

// ----------------------------------------------------------------------
// The reason a control cannot be used, on the pointer AND in the document.
//
// Fifteen controls in the console carried a sentence under them saying what
// would unblock them: *Paste a Stripe price id above*, *Change the address to
// save it*, *Tick at least one vehicle to import.* Each is correct and each
// occupies a line of a card whether or not anybody is asking, which is what
// made a form of four fields read as a page of instructions.
//
// **So the sentence moves onto the pointer, and it does not leave the
// document.** A disabled control receives no pointer events (`ButtonBase`
// sets `pointer-events: none` on `.Mui-disabled`), so the hover listener hangs
// on a wrapper span, which is MUI's documented pattern and the one
// `CommandButton` already used.
//
// The tooltip is the pointer's *copy* of the sentence and never its only home.
// MUI attaches `aria-describedby` only while a tooltip is open and only to the
// element it wraps, so a tooltip alone is exactly the dead end R6 exists to
// remove: nothing for a screen reader, nothing in a screenshot, nothing for
// the rendered audit to resolve. The sentence stays, addressed by the control.
//
// **And a pointer is not the only way people read this console.** A tooltip a
// finger cannot hover is a sentence that does not exist, so on a coarse
// pointer, and on any viewport narrow enough to be one, the sentence is
// simply drawn, the way it was before. That is not a fallback bolted on
// afterwards: it is the same node, unhidden by a media query, so there is one
// sentence in one place and no second copy to drift.
// ----------------------------------------------------------------------

/**
 * Where the sentence stops being a tooltip and goes back to being a line.
 *
 * Two conditions, and either is enough. `pointer: coarse` is the honest test
 * (it asks what the person is actually pointing with), and the width test
 * catches a tablet in a keyboard case and a desktop browser dragged narrow,
 * where a tooltip is technically reachable and practically not.
 */
const NO_HOVER = `@media (pointer: coarse), (max-width: ${
  breakpoints.sm - 0.05
}px)`

/**
 * The same media query, for a layout that places the drawn sentence itself:
 * `ActionRow` puts it on a line of its own under the row.
 */
export const BLOCKED_HINT_NO_HOVER = NO_HOVER

/**
 * The exact inverse of `visuallyHidden`.
 *
 * Written out rather than spelled `{ ...visuallyHidden, position: 'static' }`:
 * that object clips, insets and collapses the box in six declarations, and
 * undoing five of them while leaving one is how a "visible" fallback ships as
 * a 1px sliver nobody notices is empty.
 */
const UNHIDDEN = {
  position: 'static',
  width: 'auto',
  height: 'auto',
  overflow: 'visible',
  clip: 'auto',
  clipPath: 'none',
  whiteSpace: 'normal',
  margin: 0,
  padding: 0,
  border: 0,
} as const

export interface BlockedHintProps {
  /**
   * The blocker and the remedy, in a sentence. `null` when nothing blocks, in
   * which case this renders its child and nothing else.
   */
  reason: ReactNode | null
  /**
   * The id the sentence carries, from `useBlockedAction` / `useBlockedReason`.
   *
   * Those hooks put `aria-describedby` on the control and hand back the
   * matching `{ id }` for the node that answers it. Given it, this draws the
   * sentence with that id and leaves the control alone. Omitted, it mints its
   * own id and puts it on the control itself.
   */
  reasonProps?: { id?: string }
  /** The control. */
  children: ReactElement
  /**
   * Draw the sentence at every width, not only where a pointer cannot hover.
   *
   * One screen sets it. The sign-in form's line is rendered unconditionally on
   * purpose: it is the one page where a person who cannot get in has nobody to
   * ask, and a hint only a hover can reach is no help to them.
   */
  alwaysVisible?: boolean
  /**
   * The sentence already exists somewhere else on the screen, so draw only the
   * tooltip.
   *
   * For the one shape this does not own: two controls addressing a single id,
   * minted once, because two ids for one fact is two chances for them to say
   * different things. `TermsValuesForm` is the case (`Discard changes` and
   * `Publish` share a blocker), and there the screen keeps the node and
   * applies `BLOCKED_HINT_HIDDEN` to it itself.
   */
  sentenceElsewhere?: boolean
  /**
   * Never draw the sentence, at any width: it stays on the pointer and in the
   * document for a screen reader, and is not a line on the page.
   *
   * For a control whose blocker is already plain from what is above it. The
   * onboarding form is the case: two labelled fields, one of them empty, and
   * on a phone the line naming it sat alone under a full-width Continue and
   * was asked to go.
   */
  sentenceHidden?: boolean
  /** The wrapper must not shrink a button the layout stretches. */
  sx?: SxProps<Theme>
}

export default function BlockedHint({
  reason,
  reasonProps,
  children,
  alwaysVisible = false,
  sentenceElsewhere = false,
  sentenceHidden = false,
  sx,
}: BlockedHintProps) {
  const own = useId()
  const blocked = reason !== null && reason !== undefined
  const id = reasonProps?.id ?? own

  // **The wrapper is drawn whether or not anything is blocked, and that is not
  // laziness.** Returning the bare child when nothing blocks changes the
  // element type at this position, so React unmounts the control and mounts a
  // new one the moment the blocker clears, which is exactly when somebody has
  // just finished typing into the form above it. A keyboard user tabbing to
  // Save would have focus thrown away by the thing that was about to let them
  // press it, and a spec holding a reference to the button would be holding a
  // detached node. An empty `title` renders no popper, so the cost of keeping
  // the shape is one span.
  //
  // The clone only reaches for `aria-describedby` when there is a sentence to
  // point at. Writing `undefined` over it would be a different thing from
  // leaving it alone: the onboarding form's submit addresses an alert already
  // on screen when the country is the blocker, and this component is handed no
  // reason in that case precisely so the alert keeps the button, so it must
  // not then erase the pointer to it.
  const control = blocked
    ? cloneElement(children as ReactElement<{ 'aria-describedby'?: string }>, {
        'aria-describedby': id,
      })
    : children

  const sentence =
    sentenceElsewhere || !blocked ? null : (
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        id={id}
        data-blocked-hint-sentence=""
        sx={
          alwaysVisible
            ? { display: 'block' }
            : sentenceHidden
              ? visuallyHidden
              : { ...visuallyHidden, [NO_HOVER]: { ...UNHIDDEN } }
        }
      >
        {reason}
      </Typography>
    )

  return (
    <Tooltip
      // Wrapped in an element rather than passed as a string, and that is the
      // whole reason it is wrapped. MUI writes a native `title` attribute onto
      // its child whenever the title it is given is a string, so with
      // `describeChild` set, the wrapper span grew exactly the browser tooltip
      // this component exists to replace: unstyled, slow, unreachable by
      // keyboard, and firing from the same box as the real one. A node is not
      // a string, so no `title` is written and nothing else changes.
      title={blocked ? <span>{reason}</span> : ''}
      // Deliberate. Without it MUI writes `aria-label={title}` onto the
      // wrapper span, which puts a stray name on a non-interactive element;
      // with it the span takes `aria-describedby` only while the tooltip is
      // open, which is inert.
      describeChild
    >
      <Box
        component="span"
        data-blocked-hint=""
        sx={[
          { display: 'inline-flex', flexDirection: 'column', gap: 0.5 },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {control}
        {sentence}
      </Box>
    </Tooltip>
  )
}

/**
 * The hidden-until-a-finger-needs-it treatment, for the one site whose
 * sentence `BlockedHint` does not own.
 *
 * `TermsValuesForm` mints a single id for a sentence two controls address, so
 * the node has to stay where it is rather than be drawn twice. This is what
 * the hint would have applied to it: out of the layout on a pointer, drawn
 * again the moment there is no pointer to hover with.
 */
export const BLOCKED_HINT_HIDDEN: SxProps<Theme> = {
  ...visuallyHidden,
  [NO_HOVER]: { ...UNHIDDEN },
}

/**
 * The stretch escape hatch, for the call sites whose button is `fullWidth` or
 * whose footer stacks with `align-items: stretch` below `sm`.
 *
 * The wrapper span is a new box between the layout and the control, so without
 * this a full-width button shrinks to its own label on a phone. Exported as a
 * value rather than left to each screen, because six sites need it and six
 * copies of a layout rule is how they stop agreeing.
 */
export const BLOCKED_HINT_STRETCH: SxProps<Theme> = {
  'display': 'flex',
  'width': { xs: '100%', sm: 'auto' },
  '& > .MuiButtonBase-root': { width: '100%' },
}
